import { useMemo, useState, type ReactNode } from 'react';
import {
    PiArrowsSplitBold,
    PiCaretDownBold,
    PiCheckCircleBold,
    PiMagnifyingGlassBold,
    PiPaletteBold,
    PiScalesBold,
    PiWarningCircleBold,
} from 'react-icons/pi';
import { cn } from '@v2/utils/cn';
import { useColorHexMaps, type ColorSlot } from '@v2/features/colors/colorHex';
import { COLOR_SLOTS, COLOR_SLOT_LABELS, ColorDot, isNoCustomization } from '@v2/features/colors/ColorDot';

export type SkuVerdict = 'ok' | 'color_split' | 'qty' | 'only_pi' | 'only_po' | 'po_no_color';

export type SkuGroup = {
    product_code: string;
    supplier_code: string | null;
    product_name: string;
    image_url: string | null;
    po_qty: number;
    pi_qty: number;
    qty_diff: number;
    wrong_color_units: number;
    verdict: SkuVerdict;
    variants: {
        colors: Record<ColorSlot, string | null>;
        pi_colors: Record<ColorSlot, string | null> | null;
        color_diff: string[];
        po_missing_colors: boolean;
        po_qty: number;
        pi_qty: number;
        row_status: string;
    }[];
};

export type SkuSummary = Record<SkuVerdict | 'total', number>;

const VERDICT: Record<SkuVerdict, { label: string; tone: string; icon: ReactNode }> = {
    qty: { label: 'Quantidade diferente', tone: 'bg-red-50 text-red-700 ring-red-200', icon: <PiScalesBold /> },
    only_pi: { label: 'Só na PI', tone: 'bg-rose-50 text-rose-700 ring-rose-200', icon: <PiWarningCircleBold /> },
    only_po: { label: 'Só na PO', tone: 'bg-slate-100 text-slate-700 ring-slate-200', icon: <PiWarningCircleBold /> },
    color_split: {
        label: 'Mesma qtd, cor diferente',
        tone: 'bg-amber-50 text-amber-800 ring-amber-200',
        icon: <PiArrowsSplitBold />,
    },
    po_no_color: { label: 'PO sem cor', tone: 'bg-sky-50 text-sky-700 ring-sky-200', icon: <PiPaletteBold /> },
    ok: { label: 'OK', tone: 'bg-emerald-50 text-emerald-700 ring-emerald-200', icon: <PiCheckCircleBold /> },
};

const ORDER: SkuVerdict[] = ['qty', 'only_pi', 'only_po', 'color_split', 'po_no_color', 'ok'];

const fmt = (n: number) => n.toLocaleString('pt-BR', { maximumFractionDigits: 2 });

function sentence(g: SkuGroup): string {
    switch (g.verdict) {
        case 'qty': {
            const diff = g.qty_diff > 0 ? `+${fmt(g.qty_diff)}` : fmt(g.qty_diff);
            const extra = g.wrong_color_units > 0 ? ` · ${fmt(g.wrong_color_units)} un. em cor que a PO não pediu` : '';
            return `PO ${fmt(g.po_qty)} un. × PI ${fmt(g.pi_qty)} un. (${diff} na PI)${extra}.`;
        }
        case 'color_split':
            return `Quantidade bate (${fmt(g.po_qty)} un.), mas ${fmt(g.wrong_color_units)} un. vieram na PI em cor diferente da PO.`;
        case 'po_no_color':
            return `A linha da PO está sem cor (não veio do Ploomes). Use "Sincronizar cores" na PO e confira de novo.`;
        case 'only_pi':
            return `${fmt(g.pi_qty)} un. na PI que não estão na PO.`;
        case 'only_po':
            return `${fmt(g.po_qty)} un. pedidas na PO que a PI não trouxe.`;
        default:
            return `PO e PI batem: ${fmt(g.po_qty)} un.`;
    }
}

function Colors({ colors, highlight }: { colors: Record<ColorSlot, string | null>; highlight?: string[] }) {
    const hexMaps = useColorHexMaps();
    const parts = COLOR_SLOTS.filter((s) => !isNoCustomization(colors[s]));
    if (parts.length === 0) return <span className="text-xs italic text-gray-400">sem cor</span>;
    return (
        <span className="flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-0.5 text-xs">
            {parts.map((slot) => (
                <span key={slot} className={cn('inline-flex items-center gap-1 whitespace-nowrap', highlight?.includes(slot) && 'font-semibold text-amber-700')}>
                    <ColorDot slot={slot} name={colors[slot]} hexMaps={hexMaps} />
                    <span className="text-gray-400">{COLOR_SLOT_LABELS[slot]}</span>
                    <span className="text-gray-800">{colors[slot]}</span>
                </span>
            ))}
        </span>
    );
}

function QtyCell({ value, other }: { value: number; other: number }) {
    return (
        <span
            className={cn(
                'w-12 shrink-0 text-right text-sm tabular-nums',
                value === 0 ? 'text-gray-300' : value !== other ? 'font-bold text-red-600' : 'font-semibold text-gray-900',
            )}
        >
            {fmt(value)}
        </span>
    );
}

export default function SkuReconciliationPanel({
    groups,
    summary,
    onFocusSku,
    totalPoQty,
    totalPiQty,
}: {
    groups: SkuGroup[];
    summary: SkuSummary;
    onFocusSku: (code: string) => void;
    totalPoQty: number;
    totalPiQty: number;
}) {
    const qtyDiff = totalPiQty - totalPoQty;
    const [verdict, setVerdict] = useState<SkuVerdict | 'problems'>('problems');
    const [expanded, setExpanded] = useState(false);

    const list = useMemo(
        () => groups.filter((g) => (verdict === 'problems' ? g.verdict !== 'ok' : g.verdict === verdict)),
        [groups, verdict],
    );
    const visible = expanded ? list : list.slice(0, 6);
    const problems = summary.total - summary.ok;

    return (
        <section className="rounded-xl border border-gray-200 bg-white shadow-sm">
            <header className="flex flex-wrap items-center gap-2 border-b border-gray-100 px-4 py-3">
                <div className="mr-auto">
                    <h2 className="text-sm font-semibold text-gray-900">Conferência por SKU</h2>
                    <p className="text-xs text-gray-500">
                        {summary.total} códigos · PO <strong className="text-gray-700">{fmt(totalPoQty)} un.</strong> × PI{' '}
                        <strong className="text-gray-700">{fmt(totalPiQty)} un.</strong>
                        {qtyDiff !== 0 && (
                            <span className="font-semibold text-red-600">
                                {' '}
                                ({qtyDiff > 0 ? '+' : ''}
                                {fmt(qtyDiff)} na PI)
                            </span>
                        )}{' '}
                        · cada código é somado nos dois lados antes de olhar as cores.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => setVerdict('problems')}
                    className={cn(
                        'rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset',
                        verdict === 'problems' ? 'bg-gray-900 text-white ring-gray-900' : 'text-gray-600 ring-gray-200 hover:bg-gray-50',
                    )}
                >
                    Com divergência {problems}
                </button>
                {ORDER.filter((v) => summary[v] > 0).map((v) => (
                    <button
                        key={v}
                        type="button"
                        onClick={() => setVerdict(verdict === v ? 'problems' : v)}
                        className={cn(
                            'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset transition',
                            VERDICT[v].tone,
                            verdict === v && 'ring-2 ring-offset-1',
                        )}
                    >
                        {VERDICT[v].icon}
                        {VERDICT[v].label} {summary[v]}
                    </button>
                ))}
            </header>

            {list.length === 0 ? (
                <p className="px-4 py-6 text-center text-sm text-emerald-700">Todos os SKUs batem entre PO e PI.</p>
            ) : (
                <ul className="divide-y divide-gray-100">
                    {visible.map((g) => (
                        <li key={g.product_code} className="px-4 py-3">
                            <div className="flex flex-wrap items-start gap-3">
                                {g.image_url ? (
                                    <img src={g.image_url} alt="" className="size-11 shrink-0 rounded-lg border border-gray-100 object-contain" />
                                ) : (
                                    <span className="size-11 shrink-0 rounded-lg bg-gray-100" />
                                )}
                                <div className="min-w-0 flex-1">
                                    <p className="flex flex-wrap items-center gap-2 text-sm">
                                        <span className="font-mono font-semibold text-gray-900">{g.product_code}</span>
                                        {g.supplier_code && <span className="font-mono text-xs text-gray-400">{g.supplier_code}</span>}
                                        <span
                                            className={cn(
                                                'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset',
                                                VERDICT[g.verdict].tone,
                                            )}
                                        >
                                            {VERDICT[g.verdict].icon}
                                            {VERDICT[g.verdict].label}
                                        </span>
                                    </p>
                                    <p className="truncate text-xs text-gray-500">{g.product_name}</p>
                                    <p className="mt-1 text-xs text-gray-700">{sentence(g)}</p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => onFocusSku(g.product_code)}
                                    className="inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-primary hover:bg-primary/10"
                                >
                                    <PiMagnifyingGlassBold /> Ver linhas
                                </button>
                            </div>

                            {g.verdict !== 'ok' && g.variants.length > 0 && (
                                <div className="mt-2 overflow-hidden rounded-lg border border-gray-100 sm:ml-14">
                                    <div className="flex items-center gap-3 bg-gray-50 px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                                        <span className="flex-1">Cor</span>
                                        <span className="w-12 text-right">PO</span>
                                        <span className="w-12 text-right">PI</span>
                                    </div>
                                    {g.variants.map((v, i) => (
                                        <div key={i} className="flex items-start gap-3 border-t border-gray-100 px-3 py-1.5">
                                            <div className="min-w-0 flex-1 space-y-0.5">
                                                {v.po_missing_colors ? (
                                                    <>
                                                        <p className="text-[11px] text-sky-700">
                                                            PO sem cor{v.pi_colors ? ' · na PI:' : ' (não veio do Ploomes)'}
                                                        </p>
                                                        {v.pi_colors && <Colors colors={v.pi_colors} />}
                                                    </>
                                                ) : v.pi_colors ? (
                                                    <>
                                                        <p className="flex items-start gap-1.5">
                                                            <span className="mt-0.5 w-5 shrink-0 text-[10px] font-semibold text-gray-400">PO</span>
                                                            <Colors colors={v.colors} highlight={v.color_diff} />
                                                        </p>
                                                        <p className="flex items-start gap-1.5">
                                                            <span className="mt-0.5 w-5 shrink-0 text-[10px] font-semibold text-gray-400">PI</span>
                                                            <Colors colors={v.pi_colors} highlight={v.color_diff} />
                                                        </p>
                                                    </>
                                                ) : (
                                                    <Colors colors={v.colors} />
                                                )}
                                            </div>
                                            <QtyCell value={v.po_qty} other={v.pi_qty} />
                                            <QtyCell value={v.pi_qty} other={v.po_qty} />
                                        </div>
                                    ))}
                                    <div className="flex items-center gap-3 border-t border-gray-200 bg-gray-50 px-3 py-1 text-xs font-semibold">
                                        <span className="flex-1 text-gray-500">Total do SKU</span>
                                        <span className={cn('w-12 text-right tabular-nums', g.po_qty !== g.pi_qty && 'text-red-600')}>{fmt(g.po_qty)}</span>
                                        <span className={cn('w-12 text-right tabular-nums', g.po_qty !== g.pi_qty && 'text-red-600')}>{fmt(g.pi_qty)}</span>
                                    </div>
                                </div>
                            )}
                        </li>
                    ))}
                </ul>
            )}

            {list.length > 6 && (
                <button
                    type="button"
                    onClick={() => setExpanded((v) => !v)}
                    className="flex w-full items-center justify-center gap-1 border-t border-gray-100 py-2 text-xs font-semibold text-primary hover:bg-gray-50"
                >
                    <PiCaretDownBold className={cn('transition', expanded && 'rotate-180')} />
                    {expanded ? 'Mostrar menos' : `Mostrar os ${list.length} SKUs`}
                </button>
            )}
        </section>
    );
}
